import { useState, ChangeEvent, FormEvent, useEffect, useRef } from "react";
import "form-pattern/dist/main.css";
import '../Forms/datepicker.css';
import { FieldConfig } from "../../../types/formTypes";
import characterCountInit from 'form-pattern/src/scripts/form-character-count';
import CustomDatepicker from './Datepicker';
import chevronLeftSmall from '../../../../public/assets/images/chevronleftsmall.svg';
import Dropdown from './Dropdown';
import "notifications-pattern/dist/main.css";
import lockIcon from '../../../../public/assets/images/lockSolid.svg';
import chevronRightOutlineIcon from '../../../../public/assets/images/chevronRightOutlineIcon.svg';
import 'banner-pattern/dist/main.css';
import bannerInit from 'banner-pattern/src/banner';
import ScrollToTopBtn from "../ScrollToTopBtn";
import { validateForm } from "../../../utils/validationEngine";
import ErrorBanner from "../ErrorBanner";
import UploadErrorBanner from "../../../pages/CitizenshipReimbursement/UploadDocument/ViewDetails/UploadErrorBanner";
import '../Form.css';
import { useAppSelector } from "../../../store/hooks";
import { fetchDropdownOptions } from '../../../store/slices/Tuition/tuitionApplySlice';
import { useDispatch } from "react-redux";
import api from "../../../api/axios";
import endpoints from "../../../api/endpoints";
import { safeGet } from "../../../utils/safeGet";
import { trackCustomPageView, trackFormSubmit, trackFormSave, trackFormError, trackNavigation } from '../../../services/analytics';
import { useEligibilityBanner } from './EligibilityBannerContext';

// ==================== TYPES ====================

interface FormProps<T extends Record<string, any>> {
  fields: FieldConfig[];
  title?: string;
  initialValues?: Partial<T>;
  buttons?: string[];
  onFormSubmit?: (values: T) => Promise<void> | void;
  onSave?: (values: Partial<T>, currentStep?: number) => Promise<void> | Promise<boolean> | Promise<boolean | void> | void;
  onNext?: () => void;
  onBack?: () => void;
  onClear?: () => void;
  onSearch?: (name: string, value: any, allValues?: Partial<T>) => void;
  searchResult?: any;
  searchError?: string | null;
  isSearching?: boolean;
  stepContent?: {
    body?: string;
  };
  currentStep?: number;
  isViewDetails?: boolean;
  className?: string;
  formSpacing?: string;
  isApplicantInfoStep?: boolean;
  managerInfo?: boolean;
  suppressManagerBanner?: boolean;
  suppressEligibilityBanner?: boolean;
  onValueChange?: (name: string, value: any) => void;
  onBlur?: (name: string, value: any) => void;
  externalValidationErrors?: Record<string, string>;
  schoolNameSuggestionsEndpoint?: string;
}

const EMPTY_INITIAL_VALUES: Partial<any> = {};

interface ErrorSetters {
  setFieldErrors: (errors: Record<string, string>) => void;
  setAtLeastOneError: (error: string) => void;
  setRequiredIfErrors: (errors: Record<string, string>) => void;
  setDateRangeError: (error: { field: string; message: string } | null) => void;
}

// ==================== UTILITY FUNCTIONS ====================

/**
 * Clear all form errors at once
 */
const clearAllErrors = (setters: ErrorSetters): void => {
  setters.setFieldErrors({});
  setters.setAtLeastOneError("");
  setters.setRequiredIfErrors({});
  setters.setDateRangeError(null);
};

/**
 * Handle validation errors from validation result
 */
const handleValidationErrors = (
  validationResult: any,
  setters: ErrorSetters,
  currentStep?: number
): void => {
  // Track validation errors with analytics
  const isTuitionForm = globalThis.window?.location?.pathname.includes('/tuition');
  const appType = isTuitionForm ? 'tuition' : 'citizenship';
  
  if (validationResult.atLeastOneError) {
    console.log(`📊 Analytics: Form validation error - atLeastOne`);
    trackFormError(appType, [validationResult.atLeastOneError], currentStep);
    
    setters.setAtLeastOneError(validationResult.atLeastOneError);
    setters.setRequiredIfErrors({});
    setters.setDateRangeError(null);
    setters.setFieldErrors({});
  } else if (
    validationResult.requiredIfErrors &&
    Object.keys(validationResult.requiredIfErrors).length > 0
  ) {
    const firstError = Object.values(validationResult.requiredIfErrors)[0] as string;
    console.log(`📊 Analytics: Form validation error - requiredIf`);
    trackFormError(appType, [firstError], currentStep);
    
    setters.setRequiredIfErrors(validationResult.requiredIfErrors);
    setters.setAtLeastOneError("");
    setters.setDateRangeError(null);
    setters.setFieldErrors({});
  } else if (validationResult.dateRangeError) {
    console.log(`📊 Analytics: Form validation error - dateRange`);
    trackFormError(appType, [validationResult.dateRangeError.message], currentStep);
    
    setters.setDateRangeError(validationResult.dateRangeError);
    setters.setAtLeastOneError("");
    setters.setRequiredIfErrors({});
    setters.setFieldErrors({});
  } else {
    const errorCount = Object.keys(validationResult.errors).length;
    if (errorCount > 0) {
      const errorFields = Object.keys(validationResult.errors);
      console.log(`📊 Analytics: Form validation error - ${errorCount} field errors`);
      trackFormError(appType, errorFields, currentStep);
    }
    
    setters.setFieldErrors(validationResult.errors);
    setters.setAtLeastOneError("");
    setters.setRequiredIfErrors({});
    setters.setDateRangeError(null);
  }
};

/**
 * Get field error from multiple error sources
 */
const getFieldError = (
  fieldName: string,
  fieldErrors: Record<string, string>,
  requiredIfErrors: Record<string, string>,
  dateRangeError: { field: string; message: string } | null,
  externalValidationErrors: Record<string, string>
): string | undefined => {
  return (
    fieldErrors[fieldName] ||
    requiredIfErrors[fieldName] ||
    externalValidationErrors[fieldName] ||
    (dateRangeError?.field === fieldName ? dateRangeError.message : undefined)
  );
};

/**
 * Check if field has any error
 */
const hasFieldError = (
  fieldName: string,
  fieldErrors: Record<string, string>,
  requiredIfErrors: Record<string, string>,
  dateRangeError: { field: string; message: string } | null,
  externalValidationErrors: Record<string, string>
): boolean => {
  return Boolean(
    getFieldError(fieldName, fieldErrors, requiredIfErrors, dateRangeError, externalValidationErrors)
  );
};

/**
 * Validate and format number input
 */
const validateAndFormatNumberInput = (
  value: string,
  fieldName: string,
  isIntegerOnly: boolean = false
): string => {
  if (isIntegerOnly || fieldName.startsWith('numberOfTravelDays')) {
    return value.replaceAll(/\D/g, "");
  }

  let val = value.replaceAll(/[^0-9.]/g, "");
  const parts = val.split(".");
  
  if (parts.length > 2) {
    val = parts[0] + "." + parts.slice(1).join("");
  }

  if (parts[1]?.length > 2) {
    val = parts[0] + "." + parts[1].slice(0, 2);
  }

  if (/^0+(?!\.|$)/.test(val)) {
    val = val.replace(/^0+(?!\.|$)/, "");
  }

  return val;
};

/**
 * Check if key is valid for phone input
 */
const isValidPhoneInput = (key: string): boolean => {
  const allowedKeys = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];
  const isNumber = /^\d$/.test(key);
  const isHyphen = key === '-';

  return isNumber || isHyphen || allowedKeys.includes(key);
};

/**
 * Get input mode for number inputs
 */
const getNumberInputMode = (fieldName: string): "decimal" | "numeric" | undefined => {
  return fieldName.startsWith('numberOfTravelDays') ? "numeric" : "decimal";
};

/**
 * Get step value for number inputs
 */
const getNumberInputStep = (fieldName: string): string | undefined => {
  return fieldName.startsWith('numberOfTravelDays') ? undefined : "0.01";
};

/**
 * Check if program type is non-degree
 */
const isNonDegreeProgram = (programType: string | undefined): boolean => {
  if (!programType) return false;
  const nonDegreeTypes = [
    'CEU',
    'Advanced Skills',
    'Certificate Program',
    'Certification or License',
    'Basic Skills',
    'PDU',
    'CPE',
    'CME',
  ];
  return nonDegreeTypes.some((type) =>
    programType.toLowerCase().includes(type.toLowerCase())
  );
};

/**
 * Handle program type change
 */
const handleProgramTypeChange = (
  newValue: any,
  currentDegreeYear: string | undefined,
  previousFormData: Record<string, any>
): Record<string, any> => {
  const isNonDegree = isNonDegreeProgram(newValue);
  const updated = { ...previousFormData };

  if (
    (isNonDegree && currentDegreeYear && /^\d{4}$/.test(String(currentDegreeYear))) ||
    (!isNonDegree && currentDegreeYear === 'Non-Degree Program')
  ) {
    updated.degreeCompletionYear = '';
  }

  return updated;
};

/**
 * Get options for degreeCompletionYear field
 */
/**
 * Get options for degreeCompletionYear field, including current value if in error correction or admin edit mode
 */
const getDegreeCompletionYearOptions = (
  baseOptions: any[],
  currentValue?: string,
  isErrorCorrectionMode?: boolean,
  isAdminEditMode?: boolean
): any[] => {
  let options = Array.isArray(baseOptions) && baseOptions.length > 0
    ? [...baseOptions]
    : [];

  // Always include "Non Degree Program" option for all program types
  const isObjectArray =
    options.length > 0 && typeof options[0] === 'object' && options[0] !== null && 'value' in options[0];

  const shouldIncludeCurrent = (isErrorCorrectionMode || isAdminEditMode) && currentValue && !options.some(opt => (isObjectArray ? opt.value === currentValue : opt === currentValue));

  if (isObjectArray) {
    options = [{ id: 'non-degree', value: 'Non Degree Program' }, ...options];
    if (shouldIncludeCurrent) {
      options.push({ id: currentValue, value: currentValue });
    }
  } else {
    options = ['Non Degree Program', ...options];
    if (shouldIncludeCurrent) {
      options.push(currentValue);
    }
  }
  return options;
};

/**
 * Common function to map field names to dropdown option keys
 */
const getDropdownKey = (fieldName: string): string | null => {
  if (fieldName.includes("originState") || fieldName.includes("destinationState")) {
    return "state";
  }
  if (fieldName.includes("reimbursementType")) {
    return "reimbursement_type";
  }
  if (fieldName.includes("applicationTerm")) {
    return "application_term";
  }
  return null;
};

/**
 * Handle state selection with code mapping
 */
const handleStateSelection = (
  selectedValue: any,
  fieldName: string,
  dropdownOptions: Record<string, any>,
  disabled: boolean
): any => {
  if (selectedValue === "--select--") {
    return null;
  }

  if (!disabled) {
    const dropdownKey = getDropdownKey(fieldName);
    if (dropdownKey && selectedValue) {
      const matchedItem = dropdownOptions[dropdownKey]?.find(
        (item: { id: any; value: any }) =>
          item.id === selectedValue || item.value === selectedValue
      );
      return matchedItem?.code ?? selectedValue;
    }
  }

  return selectedValue;
};

/**
 * Get display value for select field
 */
const getSelectDisplayValue = (
  fieldName: string,
  value: any,
  dropdownOptions: Record<string, any>
): string => {
  if (value) {
    const dropdownKey = getDropdownKey(fieldName);
    if (dropdownKey) {
      return (
        dropdownOptions[dropdownKey]?.find((item: { code: any }) => item.code === value)
          ?.value ?? ""
      );
    }
  }
  return value;
};

/**
 * Format select options with default "--select--" option
 */
const formatSelectOptions = (fieldOptions: any[]): any[] => {
  return [
    { label: '--select--', value: "--select--" },
    ...fieldOptions,
  ];
};

/**
 * Get button configuration based on type
 */
const getButtonConfig = (
  actualButtonType: string,
  handlers: {
    handleSave: () => void | Promise<void>;
    handleSaveAndContinue: () => void | Promise<void>;
    handleNext: () => void | Promise<void>;
    handleBack: () => void;
    handleClear: () => void;
  },
  currentStep?: number,
  isApplicantInfoStep?: boolean
): { text: string; onClick?: () => void | Promise<void>; type: 'button' | 'submit' } | null => {
  const isTuitionStep3 =
    globalThis.window?.location?.pathname.includes('/tuition') &&
    currentStep === 3;

  const buttonConfigs: Record<
    string,
    { text: string; onClick?: () => void; type: 'button' | 'submit' }
  > = {
    save: {
      text: isApplicantInfoStep ? 'Save and Continue' : 'Save',
      onClick: isApplicantInfoStep
        ? () => {
            handlers.handleSaveAndContinue();
          }
        : () => {
            handlers.handleSave();
          },
      type: 'button',
    },
    continue: {
      text: isTuitionStep3 ? 'Save and Continue' : 'Continue',
      onClick: () => {
        handlers.handleNext();
      },
      type: 'button',
    },
    next: {
      text: 'Next',
      onClick: () => {
        handlers.handleNext();
      },
      type: 'button',
    },
    back: {
      text: 'Back',
      onClick: () => {
        handlers.handleBack();
      },
      type: 'button',
    },
    clear: {
      text: 'Clear',
      onClick: () => {
        handlers.handleClear();
      },
      type: 'button',
    },
    submit: {
      text: 'Save and submit',
      type: 'submit',
    },
  };

  return buttonConfigs[actualButtonType] || null;
};

/**
 * Get actual button type for applicant info step
 */
const getActualButtonType = (
  buttonType: string,
  isApplicantInfoStep: boolean,
  isDataModified: boolean
): string => {
  if (isApplicantInfoStep && (buttonType === 'save' || buttonType === 'continue')) {
    return isDataModified ? 'save' : 'continue';
  }
  return buttonType;
};

/**
 * Get CSS classes for button
 */
const getButtonClassName = (
  actualButtonType: string,
  isBackButton: boolean = false
): string => {
  if (isBackButton) {
    return 'button -light !m-0 block !w-full min-[992px]:!w-auto self-stretch flex items-center justify-center gap-2 whitespace-nowrap leading-none back-button h-10 box-border !max-w-none';
  }

  const isPrimaryButton = ['submit', 'next', 'save', 'continue'].includes(
    actualButtonType
  );
  const responsiveSize = 'block !w-full min-[992px]:!w-auto h-10';

  return isPrimaryButton
    ? `button -primary -light ${responsiveSize}`
    : `button ${responsiveSize}`;
};

/**
 * Determine if submit button should be disabled
 */
const isSubmitDisabled = (
  currentStep: number | undefined,
  formData: Record<string, any>
): boolean => {
  return (
    currentStep === 7 &&
    (formData[10150] !== 'Yes' || formData[10151] !== 'Yes')
  );
};

/**
 * Validate and handle errors
 */
const validateAndHandleErrors = (
  fields: FieldConfig[],
  formData: Record<string, any>,
  setters: ErrorSetters,
  currentStep?: number
): boolean => {
  // const validationResult = validateForm(fields, formData);
  // Filter out disabled fields - don't validate them
  const fieldsToValidate = fields.filter(f => !f.disabled);
  
  const validationResult = validateForm(fieldsToValidate, formData);
  if (!validationResult.isValid) {
    handleValidationErrors(validationResult, setters, currentStep);
    return false;
  }

  clearAllErrors(setters);
  return true;
};

// ==================== MAIN COMPONENT ====================

const Form = <T extends Record<string, any>>({
  fields,
  title,
  initialValues = EMPTY_INITIAL_VALUES,
  buttons = ["submit"],
  onFormSubmit,
  onSave,
  onNext,
  onBack,
  onClear,
  onSearch,
  searchResult,
  searchError,
  isSearching = false,
  stepContent,
  currentStep,
  isViewDetails,
  className,
  formSpacing,
  isApplicantInfoStep = false,
  managerInfo = false,
  suppressManagerBanner = false,
  onValueChange,
  onBlur,
  externalValidationErrors = {},
  schoolNameSuggestionsEndpoint = endpoints.tuition.tuitionApply.getSchoolNameSuggestions,
}: FormProps<T>) => {
  // Debug log for troubleshooting initialValues and fields
  console.log('[Form] initialValues:', initialValues);
  console.log('[Form] fields:', fields.map(f => f.name));

  // ==================== STATE ====================

  const [formData, setFormData] = useState<Partial<T>>(initialValues);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [atLeastOneError, setAtLeastOneError] = useState<string>("");
  const [requiredIfErrors, setRequiredIfErrors] = useState<Record<string, string>>({});
  const [dateRangeError, setDateRangeError] = useState<{
    field: string;
    message: string;
  } | null>(null);
  const [isDataModified, setIsDataModified] = useState<boolean>(false);
  const [searchValidationError, setSearchValidationError] = useState<string>("");
  const [bannerVisible, setBannerVisible] = useState<boolean>(true);
  const [schoolNameSuggestions, setSchoolNameSuggestions] = useState<Record<string, string[]>>({});
  const schoolNameSuggestionControllers = useRef<Record<string, AbortController | null>>({});
  const schoolNameSuggestionTimers = useRef<Record<string, ReturnType<typeof setTimeout> | null>>({});

  useEffect(() => {
    const handleClickOutsideAutocomplete = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;

      if (target?.closest('input[aria-autocomplete="list"], .form-autocomplete-list')) {
        return;
      }

      setSchoolNameSuggestions({});
    };

    document.addEventListener('mousedown', handleClickOutsideAutocomplete);

    return () => {
      document.removeEventListener('mousedown', handleClickOutsideAutocomplete);
    };
  }, []);

  // Context-based eligibility banner dismissed state (survives remounts via FormStepper provider)
  const eligibilityBannerCtx = useEligibilityBanner();

  // ==================== REDUX STATE ====================

  const formDataFromRedux = useAppSelector((state: any) => state.citizenshipForm?.data);
  const eligibility = formDataFromRedux?.eligibility as {
    isUnionEligible: boolean;
    is90DaysTenureMet: boolean;
  } | undefined;

  const dispatch = useDispatch();
  const { dropdownOptions } = useAppSelector((state: any) => state.tuitionApply);

  const fieldLabels = fields.reduce((acc, field) => {
    acc[field.name] = field.label;
    return acc;
  }, {} as Record<string, string>);

  // ==================== ERROR SETTERS ====================

  const errorSetters: ErrorSetters = {
    setFieldErrors,
    setAtLeastOneError,
    setRequiredIfErrors,
    setDateRangeError,
  };

  // ==================== ELIGIBILITY BANNER ====================

  const getEligibilityBanner = () => {
    if (!eligibility) return null;

    const { isUnionEligible, is90DaysTenureMet } = eligibility;

    if (isUnionEligible && is90DaysTenureMet) {
      return null;
    }

    if (!isUnionEligible && !is90DaysTenureMet) {
      return {
        title:
          "You are not a member of an eligible union and have not met the 90-day tenure requirement.",
        subtitle:
          "Please confirm your union membership and ensure you meet the tenure requirement before proceeding.",
      };
    }

    if (!isUnionEligible) {
      return {
        title: "You are not a member of an eligible union.",
        subtitle:
          "Please verify your union membership status. If you believe this is incorrect, contact HR or update your union information in your profile.",
      };
    }

    if (!is90DaysTenureMet) {
      return {
        title: "You have not met the 90-day tenure requirement.",
        subtitle:
          "You can proceed once you complete 90 days of employment. Check your hire date in your profile for confirmation.",
      };
    }

    return null;
  };

  const eligibilityBannerContent = getEligibilityBanner();

  // ==================== EFFECTS ====================

   useEffect(() => {
    const initialKeys = Object.keys(initialValues || {});
    const formKeys = Object.keys(formData || {});
    const isDifferent =
      initialKeys.length !== formKeys.length ||
      initialKeys.some((k) => initialValues[k] !== formData[k]);

    if (isDifferent) {
      setFormData(initialValues);
      setIsDataModified(false);
    }
  }, [initialValues]);

  useEffect(() => {
    if (fields.some((field) => field.type === "textarea")) {
      const timer = setTimeout(() => {
        try {
          characterCountInit();
        } catch (error) {
          console.warn("Character count initialization failed:", error);
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [fields]);

  useEffect(() => {
    const timer = setTimeout(() => {
      try {
        bannerInit();
      } catch (e) {
        console.warn("bannerInit failed:", e);
      }
    }, 50);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const isTuitionPath = globalThis.window?.location?.pathname.includes("/tuition");

    // Only fetch if tuition path AND dropdownOptions is empty AND not in view details mode
    // (parent component handles fetch for view details)
    if (isTuitionPath && Object.keys(dropdownOptions).length === 0 && !isViewDetails) {
      dispatch(
        fetchDropdownOptions({
          fieldName: "all",
          applType: "AT001",
        })
      );
    }
  }, [dispatch]);

  // ==================== HANDLERS ====================

  /**
   * Handle applicant info step data modification tracking
   */
  const handleApplicantInfoModification = (name: string, newValue: any) => {
    if (!isApplicantInfoStep || (name !== "altPhone" && name !== "altEmail")) {
      return;
    }

    const initialValue = initialValues[name as keyof T];
    if (newValue === initialValue) {
      const otherField = name === "altPhone" ? "altEmail" : "altPhone";
      const otherValue = name === "altPhone" ? formData.altEmail : formData.altPhone;
      const otherInitialValue = initialValues[otherField as keyof T];
      setIsDataModified(otherValue !== otherInitialValue);
    } else {
      setIsDataModified(true);
    }
  };

  const isSchoolNameField = (fieldName: string): boolean =>
    /^(schoolName|classSchoolName|milesSchoolName)(?:_class\d+)?$/.test(fieldName);

  const fetchSchoolNameSuggestions = async (fieldName: string, inputValue: string) => {
    const trimmedValue = inputValue.trim();

    if (!isSchoolNameField(fieldName) || trimmedValue.length < 2) {
      setSchoolNameSuggestions((prev) => ({ ...prev, [fieldName]: [] }));
      schoolNameSuggestionControllers.current[fieldName]?.abort();
      return;
    }

    schoolNameSuggestionControllers.current[fieldName]?.abort();
    const controller = new AbortController();
    schoolNameSuggestionControllers.current[fieldName] = controller;

    try {
      const response = await api.get(schoolNameSuggestionsEndpoint, {
        params: { prefix: trimmedValue },
        signal: controller.signal,
      });

      const nextSuggestions = Array.isArray(response.data?.suggestions)
        ? response.data.suggestions
        : Array.isArray(response.data)
          ? response.data
          : [];

      setSchoolNameSuggestions((prev) => ({ ...prev, [fieldName]: nextSuggestions }));
    } catch (error: any) {
      if (error?.name === "AbortError" || error?.message === "canceled") {
        return;
      }

      setSchoolNameSuggestions((prev) => ({ ...prev, [fieldName]: [] }));
    } finally {
      if (schoolNameSuggestionControllers.current[fieldName] === controller) {
        schoolNameSuggestionControllers.current[fieldName] = null;
      }
    }
  };

  /**
   * Handle input change
   */
  const handleChange = (
    name: string,
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { type, value } = e.target;
    const checked = "checked" in e.target ? e.target.checked : false;
    let newValue: any = type === "checkbox" ? checked : value;

    // Format number input without manipulating e.target.value directly
    if (type === "number" && typeof newValue === "string") {
      newValue = validateAndFormatNumberInput(newValue, name);
    }

    setFormData((prev) => {
      let updated = {
        ...prev,
        [name]: newValue,
      };

      // Handle program type change
      if (name === "programType") {
        updated = {
          ...updated,
          ...handleProgramTypeChange(
            newValue,
            prev.degreeCompletionYear as string | undefined,
            updated
          ),
        };
      }

      return updated;
    });

    onValueChange?.(name, newValue);

    const isTuitionCourseNameField =
      currentStep === 5 && /^courseName(?:_class\d+)?$/.test(name);

    if (isTuitionCourseNameField && typeof newValue === "string" && newValue.length < 200 && fieldErrors[name]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }

    // Clear search validation error when user types
    if (name === "alternateNuid" && searchValidationError) {
      setSearchValidationError("");
    }

    // Handle applicant info step data modification
    handleApplicantInfoModification(name, newValue);

    if (typeof newValue === "string" && isSchoolNameField(name)) {
      if (schoolNameSuggestionTimers.current[name]) {
        clearTimeout(schoolNameSuggestionTimers.current[name] as ReturnType<typeof setTimeout>);
      }

      schoolNameSuggestionTimers.current[name] = setTimeout(() => {
        void fetchSchoolNameSuggestions(name, newValue);
      }, 250);
    }
  };

  /**
   * Handle key press for phone and number inputs
   */
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>, name: string) => {
    const input = e.target as HTMLInputElement;

    const isTuitionCourseNameField =
      currentStep === 5 && /^courseName(?:_class\d+)?$/.test(name);

    if (isTuitionCourseNameField) {
      const isModifier = e.ctrlKey || e.metaKey || e.altKey;
      const nonTypingKeys = [
        "Backspace",
        "Delete",
        "ArrowLeft",
        "ArrowRight",
        "ArrowUp",
        "ArrowDown",
        "Home",
        "End",
        "Tab",
        "Enter",
        "Escape",
      ];
      const isTypingKey = !isModifier && e.key.length === 1 && !nonTypingKeys.includes(e.key);

      if (isTypingKey && input.value.length >= 200) {
        const courseNameField = fields.find((f) => f.name === name);
        const message =
          courseNameField?.validations?.find((v) => v.type === "maxLength")?.message ||
          "Course name must not exceed 200 characters.";

        setFieldErrors((prev) => ({
          ...prev,
          [name]: message,
        }));
        e.preventDefault();
        return;
      }
    }

    // Block negative numbers and scientific notation (e, E, +, -)
    if (input.type === "number" && ["-", "e", "E", "+", "ArrowUp", "ArrowDown"].includes(e.key)) {
      e.preventDefault();
      return;
    }

    // Validate phone input
    const phoneFieldNames = ["altPhone", "empAlternatePhone", "mgrPhone", "altManagerPhone"];
    if (phoneFieldNames.includes(name) && !isValidPhoneInput(e.key)) {
      e.preventDefault();
    }
  };

  /**
   * Handle wheel event on number inputs
   */
  const handleWheel = (e: React.WheelEvent<HTMLInputElement>) => {
    if ((e.target as HTMLInputElement).type === "number") {
      (e.target as HTMLInputElement).blur();
    }
  };

  /**
   * Handle form submission
   */
  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    if (!validateAndHandleErrors(fields, formData as Record<string, any>, errorSetters, currentStep)) {
      return;
    }

    // Track form submission
    const isTuitionForm = globalThis.window?.location?.pathname.includes('/tuition');
    const appType = isTuitionForm ? 'tuition' : 'citizenship';
    console.log(`📊 Analytics: Form submitted - ${title || 'Form'}`);
    let pageViewArg = '';
    if (appType === 'citizenship') {
      pageViewArg = '3';
    } else if (appType === 'tuition') {
      pageViewArg = '7';
    }
    trackCustomPageView(pageViewArg);
    trackFormSubmit(appType, currentStep, { formTitle: title || 'Form' });

    onNext?.();
    onFormSubmit?.(formData as T);
  };

  /**
   * Handle save action
   */
  const handleSave = () => {
    trackCustomPageView(`${currentStep}`);
    if (!validateAndHandleErrors(fields, formData as Record<string, any>, errorSetters, currentStep)) {
      return;
    }

    // Track form save
    const isTuitionForm = globalThis.window?.location?.pathname.includes('/tuition');
    const appType = isTuitionForm ? 'tuition' : 'citizenship';
    console.log(`📊 Analytics: Form saved - ${title || 'Form'}`);
    trackFormSave(appType, currentStep, { formTitle: title || 'Form' });

    onSave?.(formData);
  };

  /**
   * Handle save and continue action
   */
  const handleSaveAndContinue = () => {
    trackCustomPageView(`${currentStep}`);
    if (!validateAndHandleErrors(fields, formData as Record<string, any>, errorSetters, currentStep)) {
      return;
    }

    onSave?.(formData);
    onNext?.();
  };

  /**
   * Handle next button
   */
  const handleNext = async () => {
    clearAllErrors(errorSetters);

    const isTuitionForm = globalThis.window?.location?.pathname.includes("/tuition");
    let isValid = true;

    if (isTuitionForm) {
      if ([1, 3, 4, 5, 6].includes(currentStep ?? 0)) {
        const saveResult = await onSave?.(formData, currentStep);
        if (saveResult === false) {
          isValid = false;
        }
      }
    } else {
      isValid = validateAndHandleErrors(fields, formData as Record<string, any>, errorSetters, currentStep);
    }

    if (isValid) {
      trackCustomPageView(`${currentStep}`);
    }

    if (isTuitionForm) {
      await handleTuitionFormNext();
    } else {
      handleCitizenshipFormNext();
    }
  };

  /**
   * Handle tuition form next
   */
  const handleTuitionFormNext = async () => {
    if ([1, 3, 4, 5, 6].includes(currentStep ?? 0)) {
      const saveResult = await onSave?.(formData, currentStep);

      if (saveResult === false) {
        return;
      }
    }

    onNext?.();
  };

  /**
   * Handle citizenship form next
   */
  const handleCitizenshipFormNext = () => {
    if (!validateAndHandleErrors(fields, formData as Record<string, any>, errorSetters, currentStep)) {
      return;
    }

    onNext?.();
  };

  /**
   * Handle back button
   */
  const handleBack = () => {
    clearAllErrors(errorSetters);
    trackCustomPageView(String((currentStep ?? 0) - 2));

    
    // Track back navigation
    console.log(`📊 Analytics: Form navigation - Back button`);
    const currentPath = globalThis.window.location.pathname.replaceAll('/', ':') || 'unknown';
    trackNavigation(currentPath, 'previous', 'back');
    onBack?.();
  };

  /**
   * Handle clear button
   */
  const handleClear = () => {
    trackCustomPageView(`${currentStep}`);
    onClear?.();
  };

  /**
   * Handle blur event
   */
  const handleBlur = (name: string, value: any) => {
    onBlur?.(name, value);
  };

  // ==================== FIELD HELPERS ====================

  /**
   * Determine if field is required
   */
  const isFieldRequired = (field: FieldConfig): boolean => {
    if (!field.required && (!field.validations || field.validations.length === 0)) {
      return false;
    }

    const requiredIfRule = field.validations?.find((v) => v.type === "requiredIf");
    if (requiredIfRule?.condition) {
      return requiredIfRule.condition(formData, field.name);
    }

    return field.required || false;
  };

  /**
   * Get field error message
   */
  const getFieldErrorMessage = (fieldName: string): string | undefined => {
    return getFieldError(
      fieldName,
      fieldErrors,
      requiredIfErrors,
      dateRangeError,
      externalValidationErrors
    );
  };

  /**
   * Check if field has error flag
   */
  const hasFieldErrorFlag = (fieldName: string): boolean => {
    return hasFieldError(
      fieldName,
      fieldErrors,
      requiredIfErrors,
      dateRangeError,
      externalValidationErrors
    );
  };

  // ==================== RENDER HELPERS ====================

  /**
   * Render label
   */
  const renderLabel = (label: string, required?: boolean, htmlFor?: string) => (
    <label
      htmlFor={htmlFor}
      className={`p4 -medium ${
        required ? "after:content-['*'] after:ml-1 after:text-[#D1480C]" : ""
      }`}
    >
      {label}
    </label>
  );

  /**
   * Render error description
   */
  const renderErrorDescription = (fieldName: string, errorId?: string) => {
    const error = getFieldErrorMessage(fieldName);
    if (!error) return null;

    return (
      <div className="error-description">
        <ul id={errorId ?? `${fieldName}-error-description-list-1`}>
          <li>
            <img
              src="/assets/images/errorCircleSmall.svg"
              alt=""
              width="14"
              height="14"
            />
            <span className="screenreader-only">Error</span>
            {error}
          </li>
        </ul>
      </div>
    );
  };

  /**
   * Render search result
   */
  const renderSearchResult = (field: FieldConfig) => {
    if (field.name !== "alternateNuid" || !field.isSearchBtnRequired) return null;

    if (searchResult && typeof searchResult === "object" && searchResult?.altMgrName) {
      return (
        <div
          className="mt-3 p-4 rounded"
          style={{ backgroundColor: "#d4edda", border: "1px solid #c3e6cb" }}
        >
          <div className="text-sm" style={{ color: "#155724" }}>
            <div>
              <strong>Manager Name:</strong> {String(searchResult.altMgrName)}
            </div>
          </div>
        </div>
      );
    }

    if (
      searchError &&
      typeof searchError === "string" &&
      searchError.trim() !== ""
    ) {
      return (
        <div
          className="mt-3 p-4 rounded"
          style={{ backgroundColor: "#f8d7da", border: "1px solid #f5c6cb" }}
        >
          <div className="text-sm" style={{ color: "#721c24" }}>
            Manager not found with the provided NUID.
          </div>
        </div>
      );
    }

    return null;
  };

  /**
   * Render text input
   */
  const renderTextInput = (
    field: FieldConfig,
    value: any,
    baseControlClass: string,
    disabledClasses: string
  ) => {
    const isDynamicSchoolAutocomplete = isSchoolNameField(field.name);
    const autocompleteOptions = isDynamicSchoolAutocomplete
      ? schoolNameSuggestions[field.name] ?? []
      : field.autocompleteOptions ?? [];
    const shouldRenderAutocomplete =
      (isDynamicSchoolAutocomplete ||
        (Array.isArray(field.autocompleteOptions) && field.autocompleteOptions.length > 0)) &&
      (field.type === "text" || field.type === "email");
    const autocompleteListId = shouldRenderAutocomplete ? `${field.name}-options` : undefined;
    const hasAutocompleteOptions = shouldRenderAutocomplete && autocompleteOptions.length > 0;

    const handleAutocompleteSelect = (selectedValue: string) => {
      setFormData((prev) => ({ ...prev, [field.name]: selectedValue }));
      onValueChange?.(field.name, selectedValue);
      setSchoolNameSuggestions((prev) => ({ ...prev, [field.name]: [] }));
    };

    return (
      <div
        className="ds-form w-full"
        data-ds-theme="vessel"
        data-ds-variant="input"
        data-ds-version="1"
      >
        {renderLabel(field.label, isFieldRequired(field), field.name)}
        <div className="w-full relative">
          <div
            className={`flex items-center ${field.isSearchBtnRequired ? "gap-2" : ""}`}
          >
            <input
              autoComplete="on"
              className={`${baseControlClass} ${disabledClasses} ${
                field.disabled ? "pr-10" : ""
              } ${hasFieldErrorFlag(field.name) ? "field-error" : ""} ${
                field.isSearchBtnRequired ? "flex-1" : ""
              }`}
              disabled={field.disabled}
              style={
                field.isSearchBtnRequired ? { width: "auto" } : { width: "100%" }
              }
              id={field.name}
              name={field.name}
              type={field.type}
              aria-autocomplete={shouldRenderAutocomplete ? "list" : undefined}
              aria-controls={hasAutocompleteOptions ? autocompleteListId : undefined}
              aria-expanded={shouldRenderAutocomplete ? hasAutocompleteOptions : undefined}
              maxLength={
                currentStep === 5 && /^courseName(?:_class\d+)?$/.test(field.name)
                  ? 200
                  : undefined
              }
              inputMode={getNumberInputMode(field.name)}
              step={getNumberInputStep(field.name)}
              value={value as string}
              onChange={(e) => handleChange(field.name, e)}
              onBlur={(e) => handleBlur(field.name, e.target.value)}
              onKeyDown={(e) => handleKeyPress(e, field.name)}
              onWheel={handleWheel}
              placeholder={field.placeholder}
              aria-describedby={`${field.name}-error-description-list-1`}
              aria-invalid={hasFieldErrorFlag(field.name) ? "true" : "false"}
            />

            {hasAutocompleteOptions && (
              <ul
                id={autocompleteListId}
                className="form-autocomplete-list"
                role="listbox"
              >
                {autocompleteOptions.map((option) => (
                  <li
                    key={option}
                    className="form-autocomplete-option-wrapper"
                    role="option"
                    aria-selected={value === option}
                  >
                    <button
                      type="button"
                      className="form-autocomplete-option drop-menu-list-text"
                      onClick={() => handleAutocompleteSelect(option)}
                    >
                      {option}
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {field.isSearchBtnRequired && (
              <button
                type="button"
                className="button -primary whitespace-nowrap"
                onClick={() => {
                  const val = formData[field.name as keyof T];
                  if (!val || String(val).trim() === "") {
                    setSearchValidationError(
                      "Please enter a Alternate NUID to search"
                    );
                    return;
                  }
                  setSearchValidationError("");
                  onSearch?.(field.name, val, formData);
                }}
                disabled={field.disabled || isSearching}
              >
                {isSearching ? "Searching..." : "Change self-reported manager"}
              </button>
            )}
          </div>

          {renderSearchResult(field)}

          {hasFieldErrorFlag(field.name) ? (
            renderErrorDescription(field.name)
          ) : (
            <span id="helpertext" className="ds-form__text-input-helper">
              {field.helperText}
            </span>
          )}

          {field.disabled && (
            <img
              src={lockIcon}
              alt=""
              aria-hidden="true"
              className="absolute right-[12px] top-[12px]"
            />
          )}
        </div>
      </div>
    );
  };

  /**
   * Render textarea
   */
  const renderTextarea = (
    field: FieldConfig,
    value: any,
    baseControlClass: string,
    disabledClasses: string
  ) => {
    const maxLength = field.maxLength ?? 500;
    const textareaId = `textarea-${field.name}`;
    const labelId = `textarea-label-${field.name}`;
    const countId = `textarea-count-${field.name}`;
    const errorId = `textarea-error-${field.name}`;

    return (
      <div className="form-group w-full">
        {renderLabel(field.label, isFieldRequired(field), field.name)}
        <div className="textarea-wrapper w-full relative">
          <textarea
            autoComplete="on"
            id={textareaId}
            name={field.name}
            aria-labelledby={`${labelId} ${countId}`}
            aria-describedby={hasFieldErrorFlag(field.name) ? errorId : undefined}
            aria-invalid={hasFieldErrorFlag(field.name) ? "true" : "false"}
            className={`text-area_word-count text-area_global -book !text-base !text-[#0D1C3D] !w-full ${baseControlClass} ${disabledClasses} ${
              hasFieldErrorFlag(field.name) ? "field-error" : ""
            }`}
            style={{ width: "100%", color: "black" }}
            data-show-lines="5"
            value={value as string}
            onChange={(e) => handleChange(field.name, e)}
            disabled={field.disabled}
          />
          {field.disabled && (
            <img
              src={lockIcon}
              alt=""
              aria-hidden="true"
              className="absolute right-3 bottom-6"
            />
          )}
          {hasFieldErrorFlag(field.name) ? (
            renderErrorDescription(field.name, errorId)
          ) : (
            <div className="character-count">
              <span
                id={countId}
                aria-live="polite"
                aria-atomic="true"
                className="count-area text-input-helper"
                data-description={` of ${maxLength} characters left`}
                data-full-text={`No more characters available, ${maxLength} of ${maxLength} characters left`}
              >
                {`${
                  maxLength >= (value as string).length
                    ? maxLength - (value as string).length
                    : 0
                } of ${maxLength} characters left`}
              </span>
            </div>
          )}
        </div>
      </div>
    );
  };

  /**
   * Render date field
   */
  const renderDateField = (field: FieldConfig, value: any) => {
    return (
      <div
        className="ds-form w-full relative"
        data-ds-theme="vessel"
        data-ds-variant="input"
        data-ds-version="1"
      >
        {renderLabel(field.label, isFieldRequired(field))}
        <CustomDatepicker
          name={field.name}
          value={value as string}
          error={getFieldErrorMessage(field.name)}
          helperText={field.helperText}
          onChange={(n, v) => {
            setFormData((prev) => ({ ...prev, [n]: v }));
            onValueChange?.(n, v);
          }}
          onBlur={() => {
            onBlur?.(field.name, value);
          }}
          onDateSelected={(n, v) => {
            // Calendar selection - call blur handler with the selected date
            onBlur?.(n, v);
          }}
          disabled={field.disabled}
        />
      </div>
    );
  };

  /**
   * Render radio field
   */
  const renderRadioField = (field: FieldConfig, value: any) => {
    return (
      <div
        className="ds-form w-full min-[992px]:col-span-2"
        data-ds-theme="vessel"
        data-ds-variant="radio"
        data-ds-version="1"
      >
        <fieldset className="fieldset">
          <p className="!mb-2 p3 -book">
            {field.label}
            {field.required && <span style={{ color: "#D1480C" }}> *</span>}
          </p>
          {field.helperText && (
            <span
              id={`${field.name}-helper`}
              className="ds-form__text-input-helper block mb-3"
            >
              {field.helperText}
            </span>
          )}
          <div className="flex flex-col gap-2">
            {field.options?.map((option) => {
              if (!option) return null;
              const radioId = `${field.name}-${option
                .toLowerCase()
                .replaceAll(/\s+/g, "-")}`;
              return (
                <label
                  key={option}
                  htmlFor={radioId}
                  className="inline-flex items-center gap-2 pl-8 lg:pl-0"
                >
                  <input
                    type="radio"
                    id={radioId}
                    name={field.name}
                    value={option}
                    checked={value === option}
                    onChange={(e) => handleChange(field.name, e)}
                    disabled={field.disabled}
                    className="radio-button"
                    style={{
                      zIndex: 1000,
                      position: 'relative' as const,
                    }}
                    aria-describedby={`${field.name}-helper`}
                  />
                  <span className="label-ds2 ml-6">{option}</span>
                </label>
              );
            })}
          </div>
          {renderErrorDescription(field.name)}
        </fieldset>
      </div>
    );
  };

  /**
   * Render select field
   */
  const renderSelectField = (
    field: FieldConfig,
    value: any,
    baseControlClass: string,
    disabledClasses: string
  ) => {
    let fieldOptions = field.dropDownKey
      ? safeGet(dropdownOptions[field.dropDownKey], [])
      : safeGet(field.options, []);;

    // Handle degree completion year
    if (field.name === "degreeCompletionYear") {
      fieldOptions = getDegreeCompletionYearOptions(fieldOptions);
    }

    const options = formatSelectOptions(fieldOptions);

    return (
      <div
        className="ds-form w-full"
        data-ds-theme="vessel"
        data-ds-variant="select"
        data-ds-version="1"
      >
        <div className="w-full relative">
          {renderLabel(field.label, field.required)}
          <Dropdown
            fieldSelect={true}
            fieldName={field.name}
            selectedValue={getSelectDisplayValue(field.name, value, dropdownOptions)}
            options={options}
            onSelect={(v: any) => {
              const valueToStore = handleStateSelection(
                v,
                field.name,
                dropdownOptions,
                field.disabled ?? false
              );

              // Log selection specifically for applicationTerm for debugging
              if (field.name === 'empPrefEmail') {
                console.log('empPrefEmail select raw value:', v, '-> stored value:', valueToStore);
              }
              if (field.name === 'applicationTerm') {
                console.log('applicationTerm select raw value:', v, '-> stored value:', valueToStore);
              }

              setFormData((prev) => ({
                ...prev,
                [field.name]: valueToStore,
              }));

              onValueChange?.(field.name, valueToStore);
            }}
            className={`${baseControlClass} ${disabledClasses} w-full`}
            placeholder={field.placeholder}
            label={""}
            hasError={hasFieldErrorFlag(field.name)}
            disabled={field.disabled ?? false}
          />

          {hasFieldErrorFlag(field.name) ? (
            renderErrorDescription(field.name)
          ) : (
            <span id="helpertext" className="ds-form__text-input-helper">
              {field.helperText}
            </span>
          )}
        </div>
      </div>
    );
  };

  /**
   * Render field based on type
   */
  const renderField = (field: FieldConfig) => {
    const value = formData[field.name as keyof T] ?? "";
    const baseControlClass = "w-full min-w-0 block";
    const disabledClasses = field.disabled
      ? "ds-form__disabled-field bg-gray-100 text-gray-600"
      : "";

    switch (field.type) {
      case "text":
      case "number":
      case "email":
        return renderTextInput(field, value, baseControlClass, disabledClasses);

      case "textarea":
        return renderTextarea(field, value, baseControlClass, disabledClasses);

      case "date":
        return renderDateField(field, value);

      case "radio":
        return renderRadioField(field, value);

      case "select":
        return renderSelectField(field, value, baseControlClass, disabledClasses);

      default:
        return null;
    }
  };

  /**
   * Render button
   */
  const renderButton = (buttonType: string) => {
    const actualButtonType = getActualButtonType(
      buttonType,
      isApplicantInfoStep,
      isDataModified
    );

    const buttonConfig = getButtonConfig(
      actualButtonType,
      {
        handleSave,
        handleSaveAndContinue,
        handleNext,
        handleBack,
        handleClear,
      },
      currentStep,
      isApplicantInfoStep
    );

    if (!buttonConfig) return null;

    const isBackButton = actualButtonType === "back";
    const className = getButtonClassName(actualButtonType, isBackButton);

    return (
      <button
        key={buttonType}
        type={buttonConfig.type}
        onClick={buttonConfig.onClick}
        className={`${className} !m-0 self-stretch flex items-center justify-center gap-2 box-border !max-w-none`}
        disabled={
          actualButtonType === "submit"
            ? isSubmitDisabled(currentStep, formData)
            : false
        }
        data-analytics-type="button"
        data-analytics-click="Default"
      >
        {isBackButton && (
          <img
            src={chevronLeftSmall}
            alt=""
            className="w-5 h-5 back-arrow"
            aria-hidden="true"
          />
        )}
        <span className="inline-block align-middle">{buttonConfig.text}</span>
        {["next", "continue", "save"].includes(actualButtonType) && (
          <img
            src={chevronRightOutlineIcon}
            alt="next icon"
            className="ml-2"
          />
        )}
      </button>
    );
  };

  // ==================== MAIN RENDER ====================

  const rootClass = ["max-w-none", className].filter(Boolean).join(" ");

  return (
    <div className={rootClass}>
      {/* Eligibility Banner */}
      {currentStep === 3
  && bannerVisible
  && eligibilityBannerCtx.visible
  && eligibilityBannerContent && (
  <div
    className="ds-banner ds-banner--initialized !bg-[#FFFFFF] !border-[#D3D6DC] ds-banner__bg--pebble ds-banner--icon !mb-6"
    //                  ↑ also add ds-banner--initialized to prevent bannerInit DOM conflict
    data-ds-variant="basic"
    data-ds-version="1"
    data-ds-theme="vessel"
    aria-labelledby="eligibility-title"
  >
    <div className="ds-banner__icon-img">
      <img src="/assets/images/alertSolid.svg" alt="alert-icon" />
    </div>
    <div className="ds-banner__container">
      <button
        type="button"
        className="ds-banner__close"
        aria-label="close"
        onClick={(e) => {
          e.stopPropagation();
          setBannerVisible(false);          // local dismiss (works without a provider in tests)
          eligibilityBannerCtx.dismiss();  // also notify FormStepper via context
        }}
      >
        <img src="/assets/images/closeIcon.svg" alt="close-icon" />
      </button>
      <div className="ds-banner__main">
        <div
          className="ds-banner__main-header--announcement"
          id="eligibility-title"
        >
          {eligibilityBannerContent.title}
        </div>
        <div className="ds-banner__main-body">
          {eligibilityBannerContent.subtitle}
        </div>
      </div>
    </div>
  </div>
)}

      {/* Search Validation Error */}
      {searchValidationError && (
        <UploadErrorBanner
          header="Alternate NUID required"
          message={searchValidationError}
          className="!mt-0"
        />
      )}

      {/* Validation Error Banner */}
      {(Object.keys(fieldErrors).length > 0 ||
        Object.keys(requiredIfErrors).length > 0 ||
        atLeastOneError ||
        dateRangeError) &&
        (currentStep === 1 || currentStep === 3) && (
          <ErrorBanner
            currentStep={currentStep}
            errors={fieldErrors}
            fieldLabels={fieldLabels}
            atLeastOneError={atLeastOneError}
            requiredIfErrors={requiredIfErrors}
            dateRangeError={dateRangeError}
          />
        )}

      {/* Manager Information Banner */}
      {currentStep === 2 && !suppressManagerBanner && (
        <div
          className="ds-banner ds-banner__bg--white ds-banner--icon !p-[24px] !mb-[24px]"
          data-ds-variant="basic"
          data-ds-version="1"
          data-ds-theme="vessel"
          aria-labelledby="title-4"
          style={{ borderColor: "#003B71" }}
        >
          <div className="ds-banner__icon-img">
            <img
              src="/assets/images/informationIcon.svg"
              alt=""
              style={{ width: "42px", height: "42px" }}
            />
          </div>
          <div className="ds-banner__container">
            <div className="ds-banner__main flex flex-col">
              <div
                className="ds-banner__main-header--awareness"
                id="title-4"
                style={{ fontWeight: 350, fontSize: "20px" }}
              >
                Manager Information
              </div>
              <div className="ds-banner__main-body" style={{ fontSize: "16px" }}>
                Your manager listed below has been identified for you from HRConnect. If this
                manager's information is incorrect, contact your direct manager/supervisor.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Title and Content */}
      <div className="flex flex-col">
        {title && <h2 className="h2">{title}</h2>}
        {stepContent?.body && (
          <div className="flex flex-col gap-4">
            {stepContent.body.split('\n\n').map((paragraph) => {
              if (
                paragraph.includes('Required fields are indicated with an asterisk')
              ) {
                const parts = paragraph.split(
                  'Required fields are indicated with an asterisk (*).'
                );
                return (
                  <p key={paragraph} className="m-0">
                    {parts[0]}
                    <span style={{ color: '#dc2626' }}>
                      Required fields are indicated with an asterisk (*).
                    </span>
                    {parts[1]}
                  </p>
                );
              }
              return (
                <p key={paragraph} className="m-0">
                  {paragraph}
                </p>
              );
            })}
          </div>
        )}
      </div>

      {/* Form */}
      <form
        onSubmit={handleSubmit}
        className={`flex flex-col gap-[32px] flex-wrap ${formSpacing} w-full`}
      >
        <div className="grid grid-cols-1 min-[992px]:!grid-cols-2 gap-x-4 gap-y-8 w-full">
          {fields.map((field: FieldConfig) => {
            const isCitizenshipPath = globalThis.window?.location?.pathname?.includes("/citizenship") ?? false;
            const shouldSpanFull = (field.type === "textarea" || field.type === "radio") && (isCitizenshipPath ? !isViewDetails : true);
            return (
              <div
                key={field.name}
                className={`w-full min-w-0 ${shouldSpanFull ? "min-[992px]:col-span-2" : ""}`}
              >
                {renderField(field)}
              </div>
            );
          })}
        </div>
        <div className="flex flex-col-reverse !gap-4 min-[992px]:!gap-3 min-[992px]:flex-row mt-12 w-full">
          {buttons.map((buttonType) => renderButton(buttonType))}
        </div>
      </form>

      {/* Scroll to Top */}
      {managerInfo || globalThis.window?.location?.pathname?.includes('/tuition') ? null : <ScrollToTopBtn />}
    </div>
  );
};

export default Form;